import { formatDistanceToNow, format } from 'date-fns';
import { Truck, Clock, AlertTriangle, Settings, Trash2, ArrowRight, Wrench, XCircle, ChevronDown, ShieldOff } from 'lucide-react';
import { getDockDisplayStatus, toManualSelectValue, getDockStatusDebug } from '../../lib/dock-utils';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { useState, useRef, useEffect } from 'react';

interface AppointmentSnippet {
    id: string;
    status: string;
    carrierName?: string;
    customerPoNumber?: string;
    startAt: string;
    checkInTime?: string;
}

interface Dock {
    id: string;
    dockNumber: string;
    type?: string;
    status: 'Available' | 'Occupied' | 'Maintenance' | 'Out of Service' | string;
    now?: AppointmentSnippet | null;
    next?: AppointmentSnippet | null;
    updatedAt?: string;
    capacity?: number;
    effectiveStatus?: 'Available' | 'Occupied' | 'Maintenance' | 'Out of Service' | string;
    // Exception Fields
    exceptionOwnerUserId?: string | null;
    exceptionReason?: string | null;
    exceptionNote?: string | null;
    exceptionResolvedAt?: string | null;
    exceptionUpdatedAt?: string | null;
}

interface DockCardProps {
    dock: Dock;
    onStatusChange: (dockId: string, status: string) => void;
    onEdit: (dock: Dock) => void;
    onDelete: (dockId: string) => void;
    onViewSchedule: (dockId: string) => void;
}

export function DockCard({ dock, onStatusChange, onEdit, onDelete, onViewSchedule }: DockCardProps) {
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [gearOpen, setGearOpen] = useState(false);
    const gearRef = useRef<HTMLDivElement>(null);

    // Close gear menu on outside click
    useEffect(() => {
        if (!gearOpen) return;
        const handler = (e: MouseEvent) => {
            if (gearRef.current && !gearRef.current.contains(e.target as Node)) setGearOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [gearOpen]);

    // 1. Determine System Status (Canonical)
    const systemStatus = getDockDisplayStatus(dock); // "Available" | "Occupied" | "Maintenance" | "Out of Service"

    // DEBUG: Verify status logic in dev IF configured via URL param
    if (import.meta.env.DEV) {
        const debugTarget = new URLSearchParams(window.location.search).get('dockDebug');
        if (debugTarget && dock.id === debugTarget) {
            console.log(`[dock-debug:${dock.id}]`, getDockStatusDebug(dock));
        }
    }

    // 2. Determine Manual Status (Stored)
    // Use the safe select-value converter to handle "oos", "busy", etc.
    const manualStatus = toManualSelectValue(dock.status);

    // 3. Detect Override
    // Show override whenever System != Manual, UNLESS manual is Maintenance/OOS (which usually wins anyway).
    // Specifically: If manual is Available/Occupied but System forces something else.
    // If manual says Maint/OOS, getDockDisplayStatus returns Maint/OOS, so they are equal.
    // So simple inequality check covers the requirements, as Maint/OOS match avoids the flag.
    const isOverride = systemStatus !== manualStatus;

    // Badge styling based on SYSTEM status
    const isOccupied = systemStatus === 'Occupied';
    const isMaintenance = systemStatus === 'Maintenance';
    const isOOS = systemStatus === 'Out of Service';
    const isAvailable = systemStatus === 'Available';



    // Dwell KPI Logic
    const getDwellDisplay = () => {
        if (!dock.now?.checkInTime) {
            return <span className="text-muted-foreground/70 italic text-[11px]">Not checked in</span>;
        }

        const checkIn = new Date(dock.now.checkInTime);
        const now = new Date();
        const diffMinutes = Math.floor((now.getTime() - checkIn.getTime()) / 60000);

        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        const text = `Dwell: ${hours}h ${minutes}m`;

        // Colors: <=2h Green, 2h-4h Amber, >4h Red
        if (diffMinutes <= 120) return <span className="text-emerald-600 dark:text-emerald-400 font-medium text-xs">{text}</span>;
        if (diffMinutes <= 240) return <span className="text-amber-600 dark:text-amber-400 font-medium text-xs">{text}</span>;
        return <span className="text-red-600 dark:text-red-500 font-bold animate-pulse text-xs">{text}</span>;
    };

    return (
        <>
            <div className={`
                relative flex flex-col h-full rounded-xl border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md
                ${isOccupied ? 'border-blue-500/30 bg-blue-500/5' : ''}
                ${isMaintenance ? 'border-amber-500/30 bg-amber-500/5' : ''}
                ${isOOS ? 'border-red-500/30 bg-red-500/5' : ''}
                ${isAvailable ? 'border-green-500/30' : ''}
            `}>
                {/* Clickable Area for Drawer */}
                <div
                    className="p-4 flex-1 cursor-pointer"
                    onClick={() => onViewSchedule(dock.id)}
                >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-xl font-bold font-mono tracking-tight">{dock.dockNumber}</h3>
                            </div>
                            {dock.updatedAt && (
                                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground/50">
                                    <span>{formatDistanceToNow(new Date(dock.updatedAt), { addSuffix: true })}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col items-end gap-1">
                            {/* Primary System Badge */}
                            <div className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${isOccupied ? 'bg-cyan-500/10 text-cyan-500' :
                                isMaintenance ? 'bg-amber-500/10 text-amber-500' :
                                    isOOS ? 'bg-red-500/10 text-red-500' :
                                        'bg-emerald-500/10 text-emerald-500'
                                }`}>
                                {systemStatus}
                            </div>

                            {/* Exception Badges */}
                            {isOccupied && dock.now?.checkInTime && (
                                (() => {
                                    const minutes = (new Date().getTime() - new Date(dock.now!.checkInTime!).getTime()) / 60000;
                                    const isCritical = minutes > 120;
                                    const isOverSLA = minutes > 60;

                                    // Has Active Exception Data? (Unresolved)
                                    const hasExceptionData = !dock.exceptionResolvedAt && (dock.exceptionNote || dock.exceptionReason || dock.exceptionOwnerUserId);

                                    if (isCritical || isOverSLA || hasExceptionData) {
                                        return (
                                            <div className="flex flex-col items-end gap-0.5 mt-1">
                                                {isCritical && (
                                                    <div className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500 text-white animate-pulse shadow-sm">
                                                        CRITICAL
                                                    </div>
                                                )}
                                                {!isCritical && isOverSLA && (
                                                    <div className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500 text-white shadow-sm">
                                                        OVER SLA
                                                    </div>
                                                )}

                                                {/* Exception Meta Badges */}
                                                {!dock.exceptionResolvedAt && (
                                                    <>
                                                        {dock.exceptionReason && (
                                                            <div className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-purple-500/10 text-purple-600 border border-purple-200">
                                                                {dock.exceptionReason}
                                                            </div>
                                                        )}
                                                        {dock.exceptionOwnerUserId && (
                                                            <div className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-500/10 text-blue-600 border border-blue-200">
                                                                Assigned
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {/* Action Link */}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onViewSchedule(dock.id); }}
                                                    className="text-[9px] underline text-muted-foreground hover:text-primary mt-0.5"
                                                >
                                                    {hasExceptionData ? 'View Exception' : 'Add Note'}
                                                </button>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()
                            )}

                            {isOverride && (
                                <div className="text-[9px] text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1" title="System status overrides manual intent (active load or backend rule).">
                                    <span>⚡ Priority Override</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* NOW Context */}
                    <div className="space-y-3">
                        <div className="min-h-[70px]">
                            <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-1 flex items-center gap-1">
                                Current Load
                                {dock.now && <span className={`w-1.5 h-1.5 rounded-full ${dock.now.status === 'in_progress' ? 'bg-blue-500 animate-pulse' : 'bg-emerald-500'}`}></span>}
                            </div>

                            {dock.now ? (
                                <div className="bg-background/50 rounded-lg p-2.5 border border-border/50">
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="font-medium flex items-center gap-1.5 truncate">
                                            <Truck className="w-3.5 h-3.5 text-primary shrink-0" />
                                            <span className="truncate">{dock.now.carrierName || 'Unknown'}</span>
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        {dock.now.customerPoNumber ? (
                                            <span className="text-[11px] text-muted-foreground font-mono">PO: {dock.now.customerPoNumber}</span>
                                        ) : (
                                            <span />
                                        )}
                                        {getDwellDisplay()}
                                    </div>
                                </div>
                            ) : isOccupied ? (
                                <div className="bg-background/50 rounded-lg p-3 border border-destructive/20 border-dashed flex items-center justify-center h-[60px] text-muted-foreground text-xs gap-2">
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                                    <span>Occupied (Manual)</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground/40 py-2 pl-1">
                                    {isMaintenance ? (
                                        <><Wrench className="w-3 h-3 text-amber-400" /><span className="text-amber-500/60">Under Maintenance</span></>
                                    ) : isOOS ? (
                                        <><XCircle className="w-3 h-3 text-red-400" /><span className="text-red-500/60">Out of Service</span></>
                                    ) : (
                                        <span className="italic">No active load</span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* NEXT Context */}
                        <div>
                            <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-1">Next Up</div>
                            {dock.next ? (
                                <div className="flex items-center justify-between text-xs p-2 rounded-md bg-secondary/30 border border-transparent">
                                    <div className="flex flex-col">
                                        <span className="font-medium">{format(new Date(dock.next.startAt), 'h:mm a')}</span>
                                        <span className="text-muted-foreground truncate max-w-[100px]">{dock.next.carrierName}</span>
                                    </div>
                                    <ArrowRight className="w-3 h-3 text-muted-foreground/50" />
                                </div>
                            ) : (
                                <div className="text-xs text-muted-foreground/40 italic pl-1">Nothing scheduled</div>
                            )}
                        </div>
                    </div>
                </div>


                {/* Actions Footer — compact */}
                <div className="border-t bg-muted/10 px-3 py-2 mt-auto">
                    <div className="flex items-center justify-between">
                        {/* Manual Status Dropdown */}
                        <div className="flex flex-col">
                            <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider font-medium mb-0.5">Dock setting</span>
                            <select
                                value={manualStatus}
                                onChange={(e) => onStatusChange(dock.id, e.target.value)}
                                className="h-6 text-[11px] rounded-md border bg-background/50 px-1.5 w-[120px] outline-none focus:ring-1 focus:ring-primary"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <option value="Available">Available</option>
                                <option value="Occupied">Occupied</option>
                                <option value="Maintenance">Maintenance</option>
                                <option value="Out of Service">Out of Service</option>
                            </select>
                        </div>

                        {/* Gear Menu */}
                        <div className="relative" ref={gearRef}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setGearOpen(!gearOpen); }}
                                className="p-1.5 hover:bg-secondary rounded-md text-muted-foreground hover:text-foreground transition-colors"
                                title="Dock actions"
                            >
                                <Settings className="w-3.5 h-3.5" />
                            </button>

                            {gearOpen && (
                                <div className="absolute right-0 bottom-8 z-50 w-44 rounded-lg border border-border bg-popover shadow-xl py-1 animate-in fade-in-0 zoom-in-95 duration-100"
                                     onClick={(e) => e.stopPropagation()}>
                                    <button
                                        onClick={() => { onEdit(dock); setGearOpen(false); }}
                                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors flex items-center gap-2"
                                    >
                                        <Settings className="w-3 h-3" /> Edit Dock
                                    </button>
                                    <button
                                        onClick={() => { onViewSchedule(dock.id); setGearOpen(false); }}
                                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors flex items-center gap-2"
                                    >
                                        <Clock className="w-3 h-3" /> View Schedule
                                    </button>
                                    <div className="border-t border-border my-1" />
                                    {!isMaintenance && !isOOS && (
                                        <button
                                            onClick={() => { onStatusChange(dock.id, 'Maintenance'); setGearOpen(false); }}
                                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors flex items-center gap-2 text-amber-600"
                                        >
                                            <Wrench className="w-3 h-3" /> Set Maintenance
                                        </button>
                                    )}
                                    {!isOOS && !isMaintenance && (
                                        <button
                                            onClick={() => { onStatusChange(dock.id, 'Out of Service'); setGearOpen(false); }}
                                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors flex items-center gap-2 text-red-600"
                                        >
                                            <XCircle className="w-3 h-3" /> Set Out of Service
                                        </button>
                                    )}
                                    {(isMaintenance || isOOS) && (
                                        <button
                                            onClick={() => { onStatusChange(dock.id, 'Available'); setGearOpen(false); }}
                                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors flex items-center gap-2 text-emerald-600"
                                        >
                                            <ChevronDown className="w-3 h-3" /> Set Available
                                        </button>
                                    )}
                                    <div className="border-t border-border my-1" />
                                    <button
                                        onClick={() => { setShowDeleteDialog(true); setGearOpen(false); }}
                                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-500/10 transition-colors flex items-center gap-2 text-red-500"
                                    >
                                        <Trash2 className="w-3 h-3" /> Delete Dock
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div >

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete dock?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(dock.id);
                                setShowDeleteDialog(false);
                            }}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
